/*****************************************************
 * Packages
 ******************************************************/
import styled from 'styled-components';
import { DndProvider } from 'react-dnd';
import React, { useState, useCallback } from 'react';
import { HTML5Backend } from 'react-dnd-html5-backend';
/*****************************************************
 * Locals
 ******************************************************/
import EditorModalBase from '../EditorModalBase';
import Tab, { TabOption } from '../../reusable/Tab';
import ContentView from './ContentView';

import { useSelector } from 'react-redux';
import * as actions from '../../../../redux/actions';
import { getAuthUser } from '../../../../redux/selectors';
import validateMembership from '../../../../util/validateMembership';
import SimpleBar from 'simplebar-react';

const Wrapper = styled.div`
    margin-top: 10px;
    background: ${({ theme }) => theme.primary.bg};
`;

// mock data
const emptyRowCol = {
    component: 'EmptyRowCol',
};

const providedSections = {
    hasDropdown: true,
    component: 'LibraryView',
    select: 'providedSections',
    title: 'Pre Designed Sections',
    request: (dispatch) => dispatch(actions.getProvidedElement('SECTION')),
};

const savedSections = {
    select: 'savedSections',
    component: 'LibraryView',
    title: 'Your Saved Sections',
    request: (dispatch) => dispatch(actions.getSavedElement('SECTION')),
};

const savedRow = {
    component: 'LibraryView',
    title: 'Your Saved Rows',
    select: 'savedRows',
    request: (dispatch) => dispatch(actions.getSavedElement('ROW')),
};

const savedColumns = {
    select: 'savedColumns',
    component: 'LibraryView',
    title: 'Your Saved Columns',
    request: (dispatch) => dispatch(actions.getSavedElement('COLUMN')),
};

const providedColumns = {
    hasDropdown: true,
    select: 'providedColumns',
    component: 'LibraryView',
    title: 'Pre Designed Columns',
    request: (dispatch) => dispatch(actions.getProvidedElement('COLUMN')),
};

const modalLibrary = {
    SECTION: [
        {
            icon: ['far', 'bars'],
            title: 'Custom Section',
            renderType: 'SECTION/ACCORDION',
            body: [
                {
                    ...emptyRowCol,
                    title: 'Section with Regular Columns',
                },
            ],
        },
        {
            renderType: 'SECTION',
            title: 'Section Library',
            icon: ['far', 'layer-group'],
            body: [{ ...providedSections }],
        },
        {
            icon: ['far', 'hdd'],
            title: 'Saved Sections',
            renderType: 'SECTION',
            body: [{ ...savedSections }],
        },
        {
            title: 'Saved Rows',
            icon: ['far', 'hdd'],
            renderType: 'SECTION',
            body: [{ ...savedRow }],
        },
    ],
    ROW: [
        {
            icon: ['far', 'bars'],
            title: 'Custom Row',
            renderType: 'SECTION',
            body: [{ ...emptyRowCol, title: 'Row with Regular Columns' }],
        },
        {
            title: 'Saved Rows',
            icon: ['far', 'hdd'],
            renderType: 'SECTION',
            body: [{ ...savedRow }],
        },
    ],
    COLUMN: [
        {
            icon: ['far', 'bars'],
            title: 'Custom Column',
            renderType: 'SECTION',
            body: [{ ...emptyRowCol, title: 'Regular Columns' }],
        },
        {
            title: 'Column Library',
            icon: ['far', 'layer-group'],
            renderType: 'SECTION',
            body: [{ ...providedColumns }],
        },
        {
            title: 'Saved Column',
            icon: ['far', 'hdd'],
            renderType: 'SECTION',
            body: [{ ...savedColumns }],
        },
    ],
    ELEMENT: [
        {
            icon: ['far', 'bars'],
            renderType: 'SECTION',
            title: 'Custom Elements',
            body: [
                {
                    title: 'General Elements',
                    component: 'DefaultElement',
                    elements: [
                        {
                            title: 'Navigation',
                            icon: ['fad', 'bars'],
                            data: {
                                type: 'nav',
                                name: 'Navigation',
                                _elType: 'ELEMENT',
                                component_path: 'nav',
                                brand: { style: { width: '100px' } },
                                btn: {
                                    items: [
                                        {
                                            id: 'yui5jpvx',
                                            label: 'Login',
                                            'style/color': '#26C281',
                                            'style/backgroundColor': '',
                                            'style/_border':
                                                '1px solid #26C281',
                                        },
                                        {
                                            id: '5xmbnhej',
                                            label: 'Join Us',
                                            'style/color': '#FFFFFF',
                                            'style/backgroundColor': '#26C281',
                                            'style/_border':
                                                '1px solid #26C281',
                                        },
                                    ],
                                    gap: '15px',
                                    style: {
                                        fontWeight: '500',
                                        borderRadius: '5px 5px 5px 5px',
                                        padding: [
                                            ['top', '10px'],
                                            ['bottom', '10px'],
                                            ['left', '15px'],
                                            ['right', '15px'],
                                        ],
                                    },
                                },
                                links: {
                                    gap: '15px',
                                    align: 'right',
                                    icon: { gap: '5px' },
                                    style: { textDecoration: { type: 'none' } },
                                    items: [
                                        { id: '2suuvmpu', label: 'Link 1' },
                                        { id: 'jrjtnw95', label: 'Link 2' },
                                        { id: 'bzdz1ay2', label: 'Link 3' },
                                    ],
                                },
                                style: {
                                    padding: [
                                        ['top', '30px'],
                                        ['bottom', '30px'],
                                    ],
                                },
                            },
                        },
                        {
                            icon: ['fad', 'heading'],
                            title: 'Heading / Title',
                            data: {
                                name: 'Title',
                                titleType: 'h1',
                                type: 'heading',
                                _elType: 'ELEMENT',
                                component_path: 'title',
                                content: 'My Page Title or Heading',
                                settings: { dynamicText: { _enabled: false } },
                                dynamicText: {
                                    items: [
                                        {
                                            id: 'hfgvo35u',
                                            content: 'Lorem ipsum',
                                        },
                                        {
                                            id: 'hfgvo35i',
                                            content: 'dolor sit amet',
                                        },
                                        {
                                            id: 'hfgvo35j',
                                            content: 'consectetur adipiscing',
                                        },
                                    ],
                                    options: {
                                        typeSpeed: 50,
                                        loop: true,
                                        backSpeed: 40,
                                        showCursor: true,
                                        cursorChar: true,
                                    },
                                },
                            },
                        },
                        {
                            title: 'Text',
                            icon: ['fad', 'align-left'],
                            data: {
                                type: 'text',
                                name: 'Text',
                                _elType: 'ELEMENT',
                                content:
                                    'These cases are perfectly simple and easy to distinguish. In a free hour, when our power of choice is untrammelled and when nothing prevents our being able to do what we like best, every pleasure is to be welcomed and every pain avoided.',
                                component_path: 'text',
                            },
                        },
                        {
                            icon: ['fad', 'icons'],
                            title: 'Icon',
                            data: {
                                type: 'icon',
                                name: 'Icon',
                                _elType: 'ELEMENT',
                                component_path: 'icon',
                                icon: {
                                    size: '30px',
                                    color: '#26C281',
                                    icon: {
                                        prefix: 'fab',
                                        iconName: 'font-awesome',
                                    },
                                },
                            },
                        },
                        {
                            icon: ['fad', 'icons'],
                            title: 'Icon + Text',
                            data: {
                                type: 'iconText',
                                name: 'Icon & Text',
                                _elType: 'ELEMENT',
                                content: 'Hello World',
                                component_path: 'iconText',
                                style: { fontSize: '18px' },
                                icon: {
                                    gap: '5px',
                                    size: '25px',
                                    color: '#26C281',
                                    icon: {
                                        prefix: 'fab',
                                        iconName: 'font-awesome',
                                    },
                                },
                            },
                        },
                        {
                            icon: ['fad', 'bullseye-pointer'],
                            title: 'Regular Button',
                            data: {
                                type: 'button',
                                name: 'Button',
                                _elType: 'ELEMENT',
                                content: 'Button Text',
                                style: {
                                    padding: [
                                        ['top', '20px'],
                                        ['bottom', '20px'],
                                        ['left', '20px'],
                                        ['right', '20px'],
                                    ],
                                    textDecoration: {
                                        type: 'none',
                                    },
                                    justifyContent: 'center',
                                    borderRadius: '5px 5px 5px 5px',
                                    border: [
                                        ['width', '0px'],
                                        ['style', ''],
                                        ['color', ''],
                                    ],
                                    backgroundColor: '#26C281',
                                    fontSize: '18px',
                                    color: '#FFFFFF',
                                    lineHeight: '18px',
                                },
                                component_path: 'button',
                            },
                        },
                        {
                            title: 'Links',
                            icon: ['fad', 'link'],
                            data: {
                                type: 'links',
                                name: 'Links',
                                _elType: 'ELEMENT',
                                component_path: 'links',
                                links: {
                                    gap: '15px',
                                    icon: { gap: '5px' },
                                    style: { textDecoration: { type: 'none' } },
                                    items: [
                                        { id: '2suuvmpu', label: 'Link 1' },
                                    ],
                                },
                            },
                        },
                        // {
                        //     icon: ["far", "bars"],
                        //     title: "App Buttons",
                        //     data: {}
                        // },
                        // MARK: countdown timer
                        {
                            title: 'Countdown Timer',
                            icon: ['fad', 'stopwatch'],
                            data: {
                                type: 'counterdown',
                                name: 'Countdown Timer',
                                _elType: 'ELEMENT',
                                component_path: 'counterdown',
                                counter: {
                                    text: {
                                        days: 'Days',
                                        hours: 'Hours',
                                        minutes: 'Months',
                                        seconds: 'Seconds',
                                    },
                                    settings: {
                                        zero: 'with-zero',
                                        onlyHours: 'days-hours',
                                        style: 'colon',
                                        size: '30px',
                                    },
                                    date: '2023-06-10',
                                    time: '00:00',
                                    GMT: '+00:00',
                                    countStyle: {
                                        fontSize: '30px',
                                        color: '#26C281',
                                    },
                                    textStyle: {
                                        fontSize: '16px',
                                        color: '#009050',
                                    },
                                    separatorStyle: {
                                        bgColor: '#26C281',
                                        colon: { fontSize: '30px' },
                                        line: { height: '30px', width: '2px' },
                                    },
                                    counterWrapperStyle: {
                                        flexDirection: 'column',
                                    },
                                    finishText: 'The countdown is over!',
                                    align: 'left',
                                },
                            },
                        },
                        {
                            icon: ['fad', 'arrows-alt-v'],
                            title: 'Accordion',
                            data: {
                                name: 'Accordion',
                                type: 'accordion',
                                _elType: 'ELEMENT',
                                component_path: 'accordion',
                                accordions: {
                                    gap: '10px',
                                    accordionsWrapperStyle: {
                                        background: '#fff',
                                        borderRadius: '5px 5px 5px 5px',
                                        _border: '2px solid #26C281',
                                        backgroundColor: '#FFFFFF',
                                    },
                                    headingStyle: {
                                        backgroundColor: '#26C281',
                                        fontSize: '18px',
                                        lineHeight: 2,
                                        padding: [
                                            ['left', '20px'],
                                            ['right', '20px'],
                                            ['top', '5px'],
                                            ['bottom', '5px'],
                                        ],
                                        color: '#FFFFFF',
                                        fontWeight: '600',
                                    },
                                    paragraphStyle: {
                                        padding: [
                                            ['left', '20px'],
                                            ['right', '20px'],
                                            ['top', '10px'],
                                            ['bottom', '10px'],
                                        ],
                                        color: '',
                                    },
                                    items: [
                                        {
                                            id: 'k75dqr70',
                                            label: 'Accordion Title 1',
                                            paragraph:
                                                '<p>Technology mass market channels network effects bandwidth seed round handshake assets interaction design business-to-consumer social media pitch virality. Sales angel investor disruptive seed money traction buyer early adopters user experience business plan bandwidth pitch growth hacking hackathon.</p>\n',
                                        },
                                        {
                                            id: '4e34ytgg',
                                            label: 'Accordion Title 2',
                                            paragraph:
                                                '<p>Technology mass market channels network effects bandwidth seed round handshake assets interaction design business-to-consumer social media pitch virality. Sales angel investor disruptive seed money traction buyer early adopters user experience business plan bandwidth pitch growth hacking hackathon.</p>\n',
                                        },
                                    ],
                                },
                            },
                        },
                        {
                            title: 'Image',
                            icon: ['fad', 'image'],
                            data: {
                                type: 'image',
                                name: 'Image',
                                _elType: 'ELEMENT',
                                component_path: 'image',
                                style: {
                                    objectFit: 'cover',
                                },
                            },
                        },
                        {
                            membership: ['PLAN_1', 'PLAN_2', 'PLAN_3'],
                            title: 'Subscription Form',
                            icon: ['fad', 'mail-bulk'],
                            data: {
                                _elType: 'ELEMENT',
                                type: 'subscription',
                                name: 'Subscription Form',
                                component_path: 'subscription',
                                settings: {
                                    service: 'mailchimp',
                                    successMessage:
                                        'Thank you for subscribing :-)',
                                },
                                fields: {
                                    email: {
                                        _enabled: true,
                                        attr: { placeholder: 'Email Address' },
                                    },
                                    firstName: {
                                        _enabled: false,
                                        attr: { placeholder: 'First Name' },
                                    },
                                    lastName: {
                                        _enabled: false,
                                        attr: { placeholder: 'Last Name' },
                                    },
                                },
                                form: {
                                    button: {
                                        content: 'Subscribe',
                                    },
                                    gap: '15px',
                                    fieldsHeight: '50px',
                                    labelStyle: {
                                        color: '#171A21',
                                        fontSize: '16px',
                                        padding: [['bottom', '10px']],
                                    },
                                    fieldStyle: {
                                        color: '#171A21',
                                        fontSize: '14px',
                                        padding: [['left', '15px']],
                                        _border: '2px solid #26C281',
                                        borderRadius: '5px 5px 5px 5px',
                                        backgroundColor: '#FFFFFF',
                                    },
                                    buttonStyle: {
                                        color: '#FFFFFF',
                                        fontSize: '18px',
                                        padding: [
                                            ['left', '30px'],
                                            ['right', '30px'],
                                        ],
                                        _border: '0px solid ',
                                        borderRadius: '5px 5px 5px 5px',
                                        backgroundColor: '#26C281',
                                    },
                                    type: 'horizontal',
                                },
                            },
                        },
                        {
                            membership: ['PLAN_1', 'PLAN_2', 'PLAN_3'],
                            title: 'Contact Form',
                            icon: ['fad', 'envelope-open-text'],
                            data: {
                                type: 'contactForm',
                                name: 'Contact Form',
                                _elType: 'ELEMENT',
                                component_path: 'contactForm',
                                fields: {
                                    name: {
                                        _enabled: false,
                                        label: 'Name',
                                        attr: {
                                            placeholder: 'Enter your name',
                                        },
                                    },
                                    email: {
                                        _enabled: true,
                                        label: 'Email',
                                        attr: {
                                            placeholder: 'Enter your email',
                                        },
                                    },
                                    phone: {
                                        _enabled: false,
                                        label: 'Phone',
                                        attr: {
                                            placeholder:
                                                'Enter your phone number',
                                        },
                                    },
                                    message: {
                                        _enabled: true,
                                        label: 'Message',
                                        attr: {
                                            rows: 4,
                                            placeholder:
                                                'Write your message...',
                                        },
                                    },
                                    subject: {
                                        _enabled: true,
                                        label: 'Subject',
                                        attr: { placeholder: 'Email Subject' },
                                    },
                                    button: {
                                        _enabled: true,
                                        text: 'Submit',
                                        waitingText: 'Please wait...',
                                    },
                                },
                                form: {
                                    gap: '15px',
                                    successMessage:
                                        'Thank you for your email :-)',
                                    labelStyle: {
                                        color: '#171A21',
                                        fontSize: '16px',
                                        padding: [['bottom', '10px']],
                                    },
                                    fieldStyle: {
                                        color: '#171A21',
                                        fontSize: '14px',
                                        minHeight: '50px',
                                        padding: [
                                            ['left', '15px'],
                                            ['right', '15px'],
                                            ['top', '10px'],
                                            ['bottom', '10px'],
                                        ],
                                        _border: '2px solid #26C281',
                                        borderRadius: '5px 5px 5px 5px',
                                        backgroundColor: '#FFFFFF',
                                    },
                                    buttonStyle: {
                                        color: '#FFFFFF',
                                        height: '50px',
                                        fontSize: '18px',
                                        padding: [
                                            ['left', '30px'],
                                            ['right', '30px'],
                                        ],
                                        _border: '0px solid ',
                                        borderRadius: '5px 5px 5px 5px',
                                        backgroundColor: '#26C281',
                                    },
                                },
                            },
                        },
                        {
                            title: 'Video',
                            icon: ['fad', 'video-plus'],
                            data: {
                                type: 'video',
                                name: 'Video',
                                _elType: 'ELEMENT',
                                component_path: 'video',
                            },
                        },
                        {
                            membership: ['PLAN_1', 'PLAN_2', 'PLAN_3'],
                            title: 'Custom Code',
                            icon: ['fad', 'code'],
                            data: {
                                type: 'code',
                                name: 'Custom Code',
                                _elType: 'ELEMENT',
                                component_path: 'code',
                            },
                        },
                        {
                            title: 'Iframe',
                            icon: ['fad', 'laptop-code'],
                            data: {
                                type: 'iframe',
                                name: 'Iframe',
                                _elType: 'ELEMENT',
                                component_path: 'iframe',
                                attr: { height: '320', width: '480' },
                            },
                        },
                        // {
                        //     icon: ["far", "bars"],
                        //     title: "Embed Video",
                        //     data: {}
                        // },
                        // {
                        //     icon: ["far", "bars"],
                        //     title: "Icon",
                        //     data: {}
                        // },
                        // {
                        //     icon: ["far", "bars"],
                        //     title: "Content Box",
                        //     data: {}
                        // },
                        {
                            icon: ['fad', 'grip-lines'],
                            title: 'Horizontal Line',
                            data: {
                                type: 'line',
                                _elType: 'ELEMENT',
                                component_path: 'line',
                                name: 'Horizontal Line',
                                style: {
                                    border: [['width', '0']],
                                    backgroundColor: 'red',
                                    height: '3px',
                                },
                            },
                        },
                        // {
                        //     icon: ["far", "bars"],
                        //     title: "Google Map",
                        //     data: {}
                        // },
                        // {
                        //     icon: ["far", "bars"],
                        //     title: "Contact Form",
                        //     data: {}
                        // }
                    ],
                },
                // {
                //     title: "Special Elements",
                //     component: "DefaultElement",
                //     elements: [
                //         {
                //             icon: ["far", "bars"],
                //             title: "Heading / Title"
                //         }
                //     ]
                // }
            ],
        },
        {
            renderType: 'SECTION',
            title: 'Element Library',
            icon: ['far', 'layer-group'],
            body: [
                {
                    select: 'providedElements',
                    component: 'LibraryView',
                    title: 'Pre Designed Elements',
                    request: (dispatch) =>
                        dispatch(actions.getProvidedElement('ELEMENT')),
                },
            ],
        },
        {
            icon: ['far', 'hdd'],
            renderType: 'SECTION',
            title: 'Saved Elements',
            body: [
                {
                    select: 'savedElements',
                    component: 'LibraryView',
                    title: 'Your Saved Elements',
                    request: (dispatch) =>
                        dispatch(actions.getSavedElement('ELEMENT')),
                },
            ],
        },
    ],
};

function AddSectionModal({ onClose, type }) {
    const user = useSelector(getAuthUser);
    const [activeTab, setActiveTab] = useState(0);
    const modalData = modalLibrary[type];
    const tabContent = modalData[activeTab];
    const handleValidateMembership = useCallback(
        (membership) => {
            return validateMembership(membership, user);
        },
        [user]
    );

    const handleTabChange = useCallback(
        (payload) => setActiveTab(payload.value),
        []
    );

    return (
        <DndProvider backend={HTML5Backend}>
            <EditorModalBase
                size="lg"
                close={onClose}
                title={`Add new ${type}`}
            >
                <Tab
                    type="underlined"
                    selected={activeTab}
                    onSelect={handleTabChange}
                >
                    {modalData.map((option, index) => (
                        <TabOption
                            value={index}
                            key={option.title}
                            icon={option.icon}
                        >
                            {option.title}
                        </TabOption>
                    ))}
                </Tab>
                <SimpleBar style={{ maxHeight: 553 }}>
                    {tabContent.body.map((item, idx) => (
                        <Wrapper key={idx}>
                            <ContentView
                                type={type}
                                data={item}
                                validateMembership={handleValidateMembership}
                            />
                        </Wrapper>
                    ))}
                </SimpleBar>
            </EditorModalBase>
        </DndProvider>
    );
}

export default AddSectionModal;
